import { ParserTerms, parser } from '@chronosphereio/editors/prometheus';
import { SyntaxNodeRef } from '@lezer/common';
import { parseTemplateVariableNames, VariableDefinition } from '@/core';
import { type VariableOpportunity } from '@/plugin-system';

export type { VariableOpportunity } from '@/plugin-system';

const { StringLiteral, LabelMatcher, Neq, NeqRegex } = ParserTerms;

interface LiteralMatcher {
  labelName: string;
  value: string;
}

/**
 * Returns true when the MatchOp for this StringLiteral is a negated operator
 * (!= or !~). Negated matchers represent exclusions and should not be offered
 * as variable-replacement candidates because the injection pipeline always
 * uses =~, which would silently reverse the matcher's semantics.
 */
function isNegatedMatcher(stringLiteralNode: SyntaxNodeRef): boolean {
  const matchOp = stringLiteralNode.node.prevSibling;
  if (!matchOp) return false;
  return matchOp.firstChild?.type.is(Neq) === true || matchOp.firstChild?.type.is(NeqRegex) === true;
}

/**
 * Resolves the label name for a StringLiteral inside a LabelMatcher.
 * Relies on the Lezer PromQL tree shape: LabelMatcher -> LabelName MatchOp StringLiteral.
 */
function resolveLabelName(node: SyntaxNodeRef, query: string): string | undefined {
  const labelNameNode = node.node.prevSibling?.prevSibling;
  if (!labelNameNode) return undefined;
  const name = query.substring(labelNameNode.from, labelNameNode.to);
  return name || undefined;
}

/**
 * Extracts literal (non-variable) label matchers from a PromQL query.
 * Returns pairs of (labelName, literalValue) for matchers that use concrete
 * string values rather than template variables.
 */
export function extractLiteralMatchers(query: string): LiteralMatcher[] {
  if (!query) return [];

  const matchers: LiteralMatcher[] = [];
  const ast = parser.parse(query);

  ast.iterate({
    enter(n: SyntaxNodeRef) {
      if (!n.type.is(StringLiteral)) return;
      if (!n.node.parent?.type.is(LabelMatcher)) return;
      if (isNegatedMatcher(n)) return;

      const value = query.substring(n.from, n.to);
      const unquoted = value.slice(1, -1);

      if (parseTemplateVariableNames(unquoted).length > 0) return;

      const labelName = resolveLabelName(n, query);
      if (!labelName) return;

      matchers.push({ labelName, value: unquoted });
    },
  });

  return matchers;
}

/**
 * Builds a map of (variableName -> Map<labelName, queryCount>) from all
 * dashboard queries, representing which variables are used on which labels
 * across the dashboard and how many distinct queries contain each pair.
 * Parses each query only once so callers don't need a separate counting pass.
 */
export function buildVariableLabelMap(
  allQueries: string[],
  variableDefinitions: VariableDefinition[]
): Map<string, Map<string, number>> {
  // Omit global/built-in names (e.g. __interval)
  const variableNames = new Set(variableDefinitions.map((d) => d.spec.name).filter((name) => !name.startsWith('__')));
  const map = new Map<string, Map<string, number>>();

  for (const query of allQueries) {
    if (!query) continue;
    const ast = parser.parse(query);

    // Track (var, label) pairs already seen in this query so a single query
    // with two identical selectors counts as 1, not 2.
    const seenInQuery = new Set<string>();

    ast.iterate({
      enter(n: SyntaxNodeRef) {
        if (!n.type.is(StringLiteral)) return;
        if (!n.node.parent?.type.is(LabelMatcher)) return;
        if (isNegatedMatcher(n)) return;

        const value = query.substring(n.from, n.to);
        const unquoted = value.slice(1, -1);
        const referencedVars = parseTemplateVariableNames(unquoted);

        if (referencedVars.length === 0) return;

        const labelName = resolveLabelName(n, query);
        if (!labelName) return;

        for (const varName of referencedVars) {
          if (!variableNames.has(varName)) continue;
          const pairKey = `${varName}\0${labelName}`;
          if (seenInQuery.has(pairKey)) continue;
          seenInQuery.add(pairKey);

          let labels = map.get(varName);
          if (!labels) {
            labels = new Map<string, number>();
            map.set(varName, labels);
          }
          labels.set(labelName, (labels.get(labelName) ?? 0) + 1);
        }
      },
    });
  }

  return map;
}

/**
 * Suggests which dashboard variables could replace literal matchers in
 * `currentQuery`, using two independent signals:
 *
 * **Cross-query (higher confidence)** — We scan `otherQueries` with
 * `buildVariableLabelMap`: wherever a matcher's *value* references a defined
 * variable (e.g. `chronosphere_namespace="$namespace"`), we record that that
 * variable is used on that matcher's *label name*. For each literal matcher in
 * the current query, if its `labelName` appears in that set for some
 * variable, we suggest replacing the literal with that variable on that
 * label. The variable's *name* does not need to equal the label name; only
 * the label must match what we learned elsewhere (e.g. var `namespace` on
 * label `chronosphere_namespace`).
 *
 * **Variable name match (fallback)** — If no cross-query pair applied, we still
 * suggest when a defined variable's name equals the literal's label name only
 * (e.g. variable `job` and literal `job="prometheus"`). This does not use
 * `otherQueries`.
 */
export function findVariableOpportunities(
  currentQuery: string,
  otherQueries: string[],
  variableDefinitions: VariableDefinition[]
): VariableOpportunity[] {
  if (!currentQuery || variableDefinitions.length === 0) return [];

  // Variables already referenced in the current query should not be suggested
  const currentVars = new Set(parseTemplateVariableNames(currentQuery));

  // Literal matchers in the current query (candidates for replacement)
  const literals = extractLiteralMatchers(currentQuery);
  if (literals.length === 0) return [];

  // Single-pass map: variable -> label -> count of distinct queries
  const variableLabelMap = buildVariableLabelMap(otherQueries, variableDefinitions);

  const seen = new Set<string>();
  const opportunities: VariableOpportunity[] = [];

  // Cross-query: pairs from variableLabelMap × literal label names in this query
  for (const [varName, labelCounts] of variableLabelMap) {
    if (currentVars.has(varName)) continue;
    for (const literal of literals) {
      const crossQueryCount = labelCounts.get(literal.labelName);
      if (crossQueryCount === undefined) continue;
      const key = `${varName}:${literal.labelName}`;
      if (seen.has(key)) continue;
      seen.add(key);

      opportunities.push({
        variableName: varName,
        labelName: literal.labelName,
        source: 'cross-query',
        crossQueryCount,
      });
    }
  }

  // Fallback: suggest when variable name equals literal label name (no otherQueries needed)
  for (const def of variableDefinitions) {
    const varName = def.spec.name;
    if (currentVars.has(varName)) continue;

    // Skip variables that start with __ (global/built-in)
    if (varName.startsWith('__')) continue;

    for (const literal of literals) {
      const key = `${varName}:${literal.labelName}`;
      if (seen.has(key)) continue;

      if (literal.labelName === varName) {
        seen.add(key);
        opportunities.push({
          variableName: varName,
          labelName: literal.labelName,
          source: 'variable-match',
          crossQueryCount: 0,
        });
      }
    }
  }

  // Sort: cross-query suggestions first (by count desc), then variable-match
  opportunities.sort((a, b) => {
    if (a.source !== b.source) {
      return a.source === 'cross-query' ? -1 : 1;
    }
    return b.crossQueryCount - a.crossQueryCount;
  });

  return opportunities;
}
